(() => {
  if (customElements.get('dg-review-feed')) return;
  const cache = new Map();
  const cacheDuration = 60000;
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  };
  const safeURL = value => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.protocol === 'http:') url.protocol = 'https:';
      return url.protocol === 'https:' ? url.href : '';
    } catch { return ''; }
  };
  async function request(url) {
    const now = Date.now();
    // Loox's shared CDN can retain a response for days. Rotate its cache key
    // each minute for both review totals and cards, sharing requests meanwhile.
    const requestURL = new URL(url);
    requestURL.searchParams.set('_dg_refresh', String(Math.floor(now / cacheDuration)));
    const key = requestURL.href;
    const cached = cache.get(key);
    if (cached && now - cached.time < cacheDuration) return cached.promise;
    for (const [cachedKey, entry] of cache) {
      if (now - entry.time >= cacheDuration) cache.delete(cachedKey);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    const promise = fetch(key, { credentials: 'omit', signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('Review request failed');
        return response.json();
      }).finally(() => clearTimeout(timer));
    cache.set(key, { time: now, promise });
    try { return await promise; } catch (error) { cache.delete(key); throw error; }
  }
  class ReviewFeed extends HTMLElement {
    connectedCallback() {
      if (this.ready || !this.isConnected) return;
      const config = this.querySelector('[data-review-labels]');
      if (!config) { requestAnimationFrame(() => this.connectedCallback()); return; }
      this.ready = true;
      this.listeners = new AbortController();
      this.labels = JSON.parse(config.textContent);
      this.home = this.dataset.mode === 'home';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      try { this.numbers = new Intl.NumberFormat(this.locale); this.dates = new Intl.DateTimeFormat(this.locale, { month: 'short', year: 'numeric', ...(!this.home && { day: 'numeric' }) }); }
      catch { this.numbers = new Intl.NumberFormat('en'); this.dates = new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }); }
      this.cards = this.querySelector('[data-review-cards]');
      this.status = this.querySelector('[data-review-status]');
      this.retry = this.querySelector('[data-review-retry]');
      this.more = [...this.querySelectorAll('[data-review-more]')];
      this.rating ??= 'all'; this.verified ??= false; this.media ??= false;
      this.sort ||= this.home ? `${this.dataset.sort || 'featured'}:desc` : 'date:desc';
      this.epoch = (this.epoch || 0) + 1;
      this.busy = false;
      this.base = `https://storefront-api.loox.io/storefront/v1/store/${encodeURIComponent(this.dataset.store)}/product-reviews`;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.matches('[data-review-more]')) this.load(false);
        if (button.matches('[data-review-retry]')) this.start();
        if (button.matches('[data-review-rating]')) {
          this.rating = button.dataset.reviewRating;
          this.querySelectorAll('[data-review-rating]').forEach(node => node.setAttribute('aria-pressed', String(node.dataset.reviewRating === this.rating)));
          this.load(true);
        }
        if (button.matches('[data-review-verified]')) { this.verified = !this.verified; button.setAttribute('aria-pressed', String(this.verified)); this.load(true); }
        if (button.matches('[data-review-media]')) { this.media = !this.media; button.setAttribute('aria-pressed', String(this.media)); this.load(true); }
        if (button.matches('[data-review-expand]')) {
          const expanded = button.getAttribute('aria-expanded') !== 'true';
          button.setAttribute('aria-expanded', String(expanded));
          button.previousElementSibling.classList.toggle('is-expanded', expanded);
          button.textContent = this.t(expanded ? 'read_less' : 'read_more');
        }
      }, { signal: this.listeners.signal });
      this.querySelector('[data-review-sort]')?.addEventListener('change', event => { this.sort = event.target.value; this.load(true); }, { signal: this.listeners.signal });
      this.start();
    }
    disconnectedCallback() { this.epoch++; this.listeners?.abort(); this.ready = false; }
    t(key, replacements = {}) {
      let value = this.labels[key] || key;
      for (const [name, replacement] of Object.entries(replacements)) value = value.replaceAll(`{${name}}`, replacement);
      return value;
    }
    stars(rating) {
      const value = Math.max(0, Math.min(5, Number(rating) || 0));
      const stars = element('span', 'dgr-stars');
      stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', this.t('rating_label', { rating: value }));
      const back = element('span', '', '★★★★★'), front = element('span', '', '★★★★★');
      back.setAttribute('aria-hidden', 'true'); front.setAttribute('aria-hidden', 'true');
      front.style.width = `${value * 20}%`; stars.append(back, front); return stars;
    }
    async start() {
      const generation = ++this.epoch;
      this.retry.hidden = true; this.status.textContent = this.t('loading');
      try {
        if (!/^[A-Za-z0-9_-]+\.[a-fA-F0-9]+$/.test(this.dataset.store || '')) throw new Error('Invalid public store ID');
        const summary = await request(this.base + '/bottomline' + (this.dataset.product ? '/' + encodeURIComponent(this.dataset.product) : ''));
        if (generation !== this.epoch || !this.isConnected) return;
        if (!Number.isFinite(summary.totalReviews) || !Number.isFinite(summary.averageRating)) throw new Error('Invalid review summary');
        this.totalReviews = summary.totalReviews;
        this.renderSummary(summary);
        await this.load(true);
      } catch { if (generation === this.epoch) this.error(); }
    }
    renderSummary(summary) {
      const box = this.querySelector('[data-review-summary]');
      box.replaceChildren();
      const score = element('div', 'dgr-score');
      if (summary.totalReviews > 0) score.append(element('strong', '', summary.averageRating.toFixed(1)));
      score.append(this.stars(summary.totalReviews ? summary.averageRating : 0));
      if (summary.totalReviews > 0) score.append(element('span', '', this.t('out_of')));
      box.append(score, element('p', 'dgr-count', `${this.numbers.format(summary.totalReviews)} ${this.t('reviews')}`));
      if (this.home && summary.totalReviews > 0) box.append(element('p', 'dgr-average', this.t('average')));
      const distribution = this.querySelector('[data-review-distribution]');
      if (!distribution) return;
      distribution.replaceChildren();
      for (let rating = 5; rating > 0; rating--) {
        const count = Math.max(0, Number(summary.ratingDistribution?.[rating]) || 0);
        const row = element('button', 'dgr-distribution-row');
        row.type = 'button'; row.dataset.reviewRating = String(rating); row.setAttribute('aria-pressed', String(this.rating === String(rating)));
        row.setAttribute('aria-label', this.t('filter_stars', { rating }) + ` (${this.numbers.format(count)})`);
        const bar = element('span', 'dgr-track'), fill = element('i');
        fill.style.width = `${summary.totalReviews ? Math.min(100, count / summary.totalReviews * 100) : 0}%`;
        bar.append(fill); row.append(element('span', '', `${rating}★`), bar, element('span', '', this.numbers.format(count)));
        distribution.append(row);
      }
    }
    async load(reset) {
      if (this.busy && !reset) return;
      const generation = ++this.epoch;
      this.busy = true; this.cards.setAttribute('aria-busy', 'true'); this.retry.hidden = true;
      this.more.forEach(button => { button.disabled = true; });
      this.status.textContent = this.t('loading');
      if (reset) { this.page = 1; this.seen = new Set(); this.hasMore = false; this.cards.replaceChildren(); }
      const filtered = this.verified || this.media;
      const limit = this.home ? 3 : filtered ? 24 : 6;
      const target = this.home ? 3 : 6;
      const fragment = document.createDocumentFragment();
      let page = this.page, hasMore = false, added = 0, batch = 0;
      const pendingIds = new Set();
      try {
        do {
          const [sort, direction] = this.sort.split(':');
          const params = new URLSearchParams({ sort, direction, page: String(page), limit: String(limit) });
          if (this.dataset.product) params.set('product_id', this.dataset.product);
          if (this.rating !== 'all') params.set('rating', this.rating);
          const data = await request(`${this.base}?${params}`);
          if (generation !== this.epoch || !this.isConnected) return;
          if (!Array.isArray(data.reviews) || !data.pagination) throw new Error('Invalid reviews');
          for (const review of data.reviews) {
            if (!review.id || this.seen.has(review.id) || pendingIds.has(review.id)) continue;
            if (this.verified && review.verified !== true) continue;
            if (this.media && !(Array.isArray(review.media) && review.media.some(item => ['photo', 'video'].includes(item.type) && safeURL(item.url)))) continue;
            fragment.append(this.card(review)); pendingIds.add(review.id); added++;
          }
          hasMore = data.pagination.hasMore === true;
          page++; batch++;
        } while (filtered && hasMore && added < target && batch < 4);
        if (generation !== this.epoch || !this.isConnected) return;
        this.cards.querySelector('.dgr-empty')?.remove();
        this.cards.append(fragment); pendingIds.forEach(id => this.seen.add(id));
        this.page = page; this.hasMore = hasMore;
        if (this.seen.size === 0) {
          const empty = element('article', 'dgr-empty');
          empty.append(element('span', 'dgr-empty-mark', '☆'));
          if (this.totalReviews === 0) {
            empty.append(element('h3', '', this.t('empty_heading')), element('p', '', this.t('empty_text')));
          } else {
            empty.append(element('h3', '', this.t(hasMore ? 'none_loaded' : 'no_match')));
          }
          this.cards.append(empty); this.status.textContent = '';
        } else {
          this.status.textContent = this.home ? '' : this.t('showing', { count: this.numbers.format(this.seen.size) });
        }
        this.more.forEach(button => { button.hidden = !hasMore; });
      } catch { if (generation === this.epoch) this.error(); }
      finally {
        if (generation === this.epoch) {
          this.busy = false; this.cards.setAttribute('aria-busy', 'false'); this.more.forEach(button => { button.disabled = false; });
        }
      }
    }
    error() {
      this.status.textContent = this.t('error'); this.retry.hidden = false;
      this.more.forEach(button => { button.hidden = true; });
    }
    card(review) {
      const card = element('article', this.home ? 'dgr-quote-card' : 'dgr-card');
      card.dataset.reviewId = String(review.id);
      const name = review.reviewer?.name || this.t('customer');
      const author = element('div', 'dgr-author');
      const picture = this.home && safeURL(review.product?.imageUrl);
      if (picture) {
        const img = element('img', 'dgr-avatar'); img.src = picture; img.alt = ''; img.loading = 'lazy'; img.width = 58; img.height = 58;
        img.addEventListener('error', () => img.replaceWith(element('span', 'dgr-initial', String(name).trim().charAt(0)))); author.append(img);
      } else author.append(element('span', 'dgr-initial', String(name).trim().charAt(0)));
      const details = element('div', 'dgr-author-details');
      const nameRow = element('div', 'dgr-name-row'); nameRow.append(element('strong', '', name));
      if (review.verified === true) nameRow.append(element('span', 'dgr-verified', `✓ ${this.t('verified_buyer')}`));
      details.append(nameRow);
      const date = new Date(review.date || review.createdAt);
      const meta = element('div', 'dgr-meta');
      if (this.home && review.product?.name) {
        const link = safeURL(review.product.url);
        const product = element(link ? 'a' : 'span', '', review.product.name);
        if (link) product.href = link;
        meta.append(product);
      }
      if (!Number.isNaN(date.valueOf())) {
        if (meta.childNodes.length) meta.append(document.createTextNode(' · '));
        const time = element('time', '', this.dates.format(date)); time.dateTime = date.toISOString(); meta.append(time);
      }
      details.append(meta); author.append(details);
      if (!this.home) card.append(author);
      card.append(this.stars(review.rating));
      const body = element(this.home ? 'blockquote' : 'p', 'dgr-body', review.body || '');
      if (this.home && review.body) { body.textContent = `“${review.body.trim()}”`; }
      card.append(body);
      if (String(review.body || '').length > (this.home ? 180 : 350)) {
        body.classList.add('is-collapsible');
        const more = element('button', 'dgr-expand', this.t('read_more')); more.type = 'button'; more.dataset.reviewExpand = ''; more.setAttribute('aria-expanded', 'false'); card.append(more);
      }
      if (this.home) card.append(author);
      else {
        const media = element('div', 'dgr-media');
        (Array.isArray(review.media) ? review.media : []).forEach(item => {
          const url = safeURL(item.url); if (!url) return;
          if (item.type === 'video') {
            const video = element('video'); video.src = url; video.controls = true; video.preload = 'none'; video.setAttribute('aria-label', this.t('video')); media.append(video);
          } else if (item.type === 'photo') {
            const link = element('a'); link.href = url; link.target = '_blank'; link.rel = 'noopener';
            const image = element('img'); image.src = url; image.alt = this.t('image'); image.loading = 'lazy'; image.width = 80; image.height = 80;
            link.append(image); media.append(link);
          }
        });
        if (media.children.length) card.append(media);
        if (review.reply?.body) { const reply = element('div', 'dgr-reply'); reply.append(element('strong', '', this.t('response')), element('p', '', review.reply.body)); card.append(reply); }
      }
      return card;
    }
  }
  customElements.define('dg-review-feed', ReviewFeed);
})();
